import React, { useState, useEffect } from 'react';

// ---------------------------------------------------------------------------
// Típusok — a backend what_if riportjának alakja.
// FONTOS: az Express közvetítő az egész payloadot snake_case-re konvertálja,
// ezért itt is snake_case kulcsokkal dolgozunk (mint a többi stream-mezőnél).
// ---------------------------------------------------------------------------

export interface WhatIfValuation {
  btc_price: number;
  eth_price: number;
  btc_value: number;
  eth_value: number;
  total: number;
}

export interface WhatIfCapitalReturn {
  returned_to_client: number;
  remaining_value: number;
  net_position: number;
}

export interface WhatIfPlan {
  label: string;
  second_deposit_usd: number;
  btc_quantity: number;
  eth_quantity: number;
  at_entry: WhatIfValuation;
  at_pumped: WhatIfValuation;
  point1_return_original_capital: WhatIfCapitalReturn;
  point2_return_both_capitals: WhatIfCapitalReturn;
  point3_btc_250k: WhatIfValuation;
  point3_btc_375k: WhatIfValuation;
}

export interface WhatIfReport {
  invested_amount_usdt: number;
  one_third_usd: number;
  two_third_usd: number;
  plan_one_third: WhatIfPlan;
  plan_two_third: WhatIfPlan;
}

// ---------------------------------------------------------------------------
// Segéd-formázók
// ---------------------------------------------------------------------------

const usd = (n: number | undefined): string =>
  `$${Math.round(n || 0).toLocaleString('en-US')}`;

const usd2 = (n: number | undefined): string =>
  `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const qty = (n: number | undefined): string => (n || 0).toFixed(6);

const signedUsd = (n: number | undefined): string => {
  const v = n || 0;
  return `${v >= 0 ? '+' : ''}${usd2(v)}`;
};

// Előjeles, USD-ben, kerekítve (a portfólió eltéréséhez).
const signedUsd0 = (n: number | undefined): string => {
  const v = n || 0;
  return `${v >= 0 ? '+' : '−'}$${Math.abs(Math.round(v)).toLocaleString('en-US')}`;
};

// ---------------------------------------------------------------------------
// Kis, újrahasznosítható UI-építőelemek
// ---------------------------------------------------------------------------

const ValuationCard: React.FC<{
  title: string;
  subtitle?: string;
  v: WhatIfValuation;
  highlight?: boolean;
}> = ({ title, subtitle, v, highlight }) => {
  const val = v || ({} as WhatIfValuation);
  return (
    <div
      className={`rounded-lg border p-4 ${
        highlight ? 'border-gray-300 bg-gray-50/80' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-xs font-semibold text-gray-700">{title}</div>
          {subtitle && <div className="text-xxs text-gray-400 mt-0.5">{subtitle}</div>}
        </div>
        <div className="text-right">
          <div className="text-sm font-bold text-gray-900 font-mono tabular-nums">
            {usd(val.total)}
          </div>
          <div className="text-xxs text-gray-400">összesen</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs font-mono tabular-nums">
        <div className="flex flex-col rounded-md bg-gray-50 px-2.5 py-1.5">
          <span className="text-xxs text-gray-400 font-sans">BTC @ {usd(val.btc_price)}</span>
          <span className="text-gray-800 font-medium">{usd2(val.btc_value)}</span>
        </div>
        <div className="flex flex-col rounded-md bg-gray-50 px-2.5 py-1.5">
          <span className="text-xxs text-gray-400 font-sans">ETH @ {usd(val.eth_price)}</span>
          <span className="text-gray-800 font-medium">{usd2(val.eth_value)}</span>
        </div>
      </div>
    </div>
  );
};

const CapitalReturnRow: React.FC<{
  step: string;
  title: string;
  description: string;
  data: WhatIfCapitalReturn;
  remainingLabel: string;
  remainingSigned?: boolean;
}> = ({ step, title, description, data, remainingLabel, remainingSigned }) => {
  const d = data || ({} as WhatIfCapitalReturn);
  const remaining = d.remaining_value || 0;
  const remainingColor = remainingSigned
    ? remaining >= 0
      ? 'text-emerald-600'
      : 'text-rose-600'
    : 'text-gray-800';

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start gap-3 mb-3">
        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white text-xxs font-bold">
          {step}
        </span>
        <div>
          <div className="text-xs font-semibold text-gray-700">{title}</div>
          <div className="text-xxs text-gray-400 mt-0.5 leading-relaxed">{description}</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs font-mono tabular-nums">
        <div className="flex flex-col rounded-md bg-gray-50 px-2.5 py-2">
          <span className="text-xxs text-gray-400 font-sans">Visszaadva</span>
          <span className="text-gray-800 font-medium">{usd2(d.returned_to_client)}</span>
        </div>
        <div className="flex flex-col rounded-md bg-gray-50 px-2.5 py-2">
          <span className="text-xxs text-gray-400 font-sans">{remainingLabel}</span>
          <span className={`font-medium ${remainingColor}`}>
            {remainingSigned ? signedUsd(remaining) : usd2(remaining)}
          </span>
        </div>
        <div className="flex flex-col rounded-md bg-gray-900 px-2.5 py-2">
          <span className="text-xxs text-gray-400 font-sans">Nettó pozíció</span>
          <span className="text-white font-semibold">{usd2(d.net_position)}</span>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Egy plan teljes levezetése (1/3 VAGY 2/3)
// ---------------------------------------------------------------------------

const PlanBody: React.FC<{
  plan: WhatIfPlan;
  originalCapital: number;
  currentValueUsd: number;
}> = ({ plan, originalCapital, currentValueUsd }) => {
  if (!plan) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-700">
        Ehhez a rekordhoz nem érkezett részletes what-if adat.
      </div>
    );
  }

  const entry = plan.at_entry || ({} as WhatIfValuation);

  // Az első befektetés mai eltérése a betett tőkéhez képest (+ vagy −).
  const portfolioDelta = (currentValueUsd || 0) - (originalCapital || 0);
  const deltaPositive = portfolioDelta >= 0;

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <div className="text-xxs uppercase tracking-wider text-gray-400 font-sans">
              Második befizetés ({plan.label})
            </div>
            <div className="text-lg font-bold text-gray-900 font-mono tabular-nums">
              {usd(plan.second_deposit_usd)}
            </div>
          </div>
          <div className="text-right text-xs font-mono tabular-nums text-gray-600">
            <div>
              <span className="text-gray-400 font-sans mr-1">BTC vásárolva:</span>
              {qty(plan.btc_quantity)}
            </div>
            <div>
              <span className="text-gray-400 font-sans mr-1">ETH vásárolva:</span>
              {qty(plan.eth_quantity)}
            </div>
          </div>
        </div>
        <div className="mt-2 text-xxs text-gray-400 leading-relaxed">
          A befizetés 50–50%-ban BTC-be (belépő {usd(entry.btc_price)}) és ETH-be (belépő{' '}
          {usd(entry.eth_price)}) kerül, levonás nélkül.
        </div>
      </div>

      <div>
        <div className="text-xxs uppercase tracking-wider text-gray-400 font-semibold mb-2">
          Értékelés
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ValuationCard
            title="Kezdeti (belépő) áron"
            subtitle="kontroll — ≈ a befizetés"
            v={plan.at_entry}
          />
          <ValuationCard
            title="Módosított áron"
            subtitle="BTC 125.000 / ETH 4700"
            v={plan.at_pumped}
            highlight
          />
        </div>
      </div>

      {/* Jelenlegi portfólió-érték (az első befektetés mai értéke) */}
      <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
        <div>
          <div className="text-xxs uppercase tracking-wider text-gray-400 font-semibold">
            Jelenlegi portfólió-érték
          </div>
          <div className="text-xxs text-gray-400 mt-0.5">
            az első befektetés mai USD értéke
          </div>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-bold text-gray-900 font-mono tabular-nums">
            {usd(currentValueUsd)}
          </span>
          <span
            className={`text-xs font-mono font-medium ${
              deltaPositive ? 'text-emerald-600' : 'text-rose-600'
            }`}
            title={`Eltérés a betett tőkéhez (${usd(originalCapital)}) képest`}
          >
            {signedUsd0(portfolioDelta)}
          </span>
        </div>
      </div>

      <div>
        <div className="text-xxs uppercase tracking-wider text-gray-400 font-semibold mb-2">
          Tőke visszaadása (Módosított árakon)
        </div>
        <div className="space-y-3">
          <CapitalReturnRow
            step="1"
            title="Csak az eredeti tőke vissza"
            description={`Visszaadjuk az eredeti tőkét (${usd(
              originalCapital
            )}). A második befizetés bent marad, a módosított áron értékelve.`}
            data={plan.point1_return_original_capital}
            remainingLabel="Bent maradt érték"
          />
          <CapitalReturnRow
            step="2"
            title="Eredeti tőke + második befizetés vissza"
            description="Visszaadjuk az eredeti tőkét és a második befizetést is. A bent maradt összeg a második befizetés növekménye."
            data={plan.point2_return_both_capitals}
            remainingLabel="Bent maradt növekmény"
            remainingSigned
          />
        </div>
      </div>
          
          {/*
      <div>
        <div className="text-xxs uppercase tracking-wider text-gray-400 font-semibold mb-2">
          Bent maradt érték magasabb BTC áron
          <span className="ml-1 normal-case font-normal text-gray-300">
            (tőke már visszaadva, ETH marad 4700)
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ValuationCard title="BTC 250 000 forgatókönyv" v={plan.point3_btc_250k} />
          <ValuationCard title="BTC 375 000 forgatókönyv" v={plan.point3_btc_375k} />
        </div>
      </div>
    
      
    */}
    </div>
  );
};

// ---------------------------------------------------------------------------
// A Modal maga
// ---------------------------------------------------------------------------

interface WhatIfModalProps {
  report: WhatIfReport | null | undefined;
  userName: string;
  depositId: number;
  currentValueUsd: number;
  onClose: () => void;
}

export const WhatIfModal: React.FC<WhatIfModalProps> = ({
  report,
  userName,
  depositId,
  currentValueUsd,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<'oneThird' | 'twoThird'>('oneThird');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!report) return null;

  const activePlan =
    activeTab === 'oneThird' ? report.plan_one_third : report.plan_two_third;

  // Fejléc-jelzés: a mai portfólió-érték eltérése a betett tőkéhez képest.
  const headerDelta = (currentValueUsd || 0) - (report.invested_amount_usdt || 0);
  const headerPositive = headerDelta >= 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-gray-900/40 backdrop-blur-sm p-4 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl my-4 bg-white border border-gray-200 rounded-xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900 tracking-tight">
              Szimulációs részletek
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {userName || 'Ismeretlen'} · #{depositId} · eredeti tőke{' '}
              <span className="font-mono text-gray-500">{usd(report.invested_amount_usdt)}</span>
              <span className="mx-1.5 text-gray-300">·</span>
              jelenleg{' '}
              <span className="font-mono text-gray-500">{usd(currentValueUsd)}</span>{' '}
              <span
                className={`font-mono ${headerPositive ? 'text-emerald-600' : 'text-rose-600'}`}
              >
                ({signedUsd0(headerDelta)})
              </span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            aria-label="Bezárás"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 pt-4">
          <div className="inline-flex rounded-lg bg-gray-100 p-1 text-xs font-medium">
            <button
              onClick={() => setActiveTab('oneThird')}
              className={`px-4 py-1.5 rounded-md transition-colors ${
                activeTab === 'oneThird'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              1/3 befizetés
              <span className="ml-1.5 font-mono text-xxs text-gray-400">
                {usd(report.one_third_usd)}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('twoThird')}
              className={`px-4 py-1.5 rounded-md transition-colors ${
                activeTab === 'twoThird'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              2/3 befizetés
              <span className="ml-1.5 font-mono text-xxs text-gray-400">
                {usd(report.two_third_usd)}
              </span>
            </button>
          </div>
        </div>

        <div className="px-6 py-5">
          <PlanBody
            plan={activePlan}
            originalCapital={report.invested_amount_usdt}
            currentValueUsd={currentValueUsd}
          />
        </div>
      </div>
    </div>
  );
};